import React, { MouseEventHandler } from 'react';

import cn from 'classnames';
import { isMobile } from 'react-device-detect';

import { isAuthenticated } from 'actions/user';
import { getSpotIndexSymbol } from 'components/basketOrders/mobileBasketOrders/mobileAssetDropdown/spotPriceComponent';
import { ORDER_SIDE } from 'constants/constants';
import { MARGIN_MODE } from 'constants/enums';
import { isMobileDeviceOnly, noop } from 'helpers/utils';
import useBasketBuySellButtonState from 'hooks/useBasketBuySellButtonState';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import {
  basketActiveUnderlyingAssetSelector,
  basketOrderSwitchSelector,
} from 'selectors/basketOrdersSelectors';
import { enabledPortfolioAssetsSelector, userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { TickIcon } from 'styles/assets/icons';
import { Product } from 'types/IProducts';

import styles from './style.module.scss';

const defaultBuyLabel = i18n.t('common:buy');
const defaultSellLabel = i18n.t('common:sell');

interface BuySellHoverButtonProps {
  product: Product;
  className?: string;
  isTableHoverButton: boolean;
  buyBtnClassName?: string;
  sellBtnClassName?: string;
  handleBtnClick?: (side: string) => void;
  onMouseOver?: MouseEventHandler<HTMLDivElement> | undefined;
  buyLabel?: string;
  sellLabel?: string;
  disableBtn?: boolean;
}

const BuySellHoverButton = ({
  product,
  className,
  isTableHoverButton,
  buyBtnClassName,
  sellBtnClassName,
  handleBtnClick,
  onMouseOver,
  buyLabel = defaultBuyLabel,
  sellLabel = defaultSellLabel,
  disableBtn = false,
}: BuySellHoverButtonProps) => {
  const { buyActive, sellActive, handleBuySellClick } = useBasketBuySellButtonState(
    product,
    isMobile || isMobileDeviceOnly() ? 'Mobile Basket Orders' : 'Options Chain'
  );

  const user = useAppSelector(userSelector);
  const mobileActiveAssetSymbol = useAppSelector(basketActiveUnderlyingAssetSelector);
  const isBasketOrdersView = useAppSelector(basketOrderSwitchSelector);
  const { whenBelowMd } = useCustomMediaQuery();
  const isLoggedIn = isAuthenticated(user);
  const enabledPortfolios = useAppSelector(enabledPortfolioAssetsSelector);

  const showButtons = () => {
    if (isLoggedIn) {
      const marginMode = user.margin_mode;
      if (
        isBasketOrdersView &&
        (marginMode === MARGIN_MODE.ISOLATED || marginMode === MARGIN_MODE.CROSS)
      ) {
        return true;
      }

      if (marginMode === MARGIN_MODE.PORTFOLIO) {
        const mobileSpotIndexSymbol = getSpotIndexSymbol(mobileActiveAssetSymbol);
        const activeAssetSymbol = isMobile
          ? mobileSpotIndexSymbol
          : product?.spot_index?.symbol;
        const isCurrentAssetPortfolioMarginEnabled =
          !!enabledPortfolios[activeAssetSymbol];
        if (isBasketOrdersView && isCurrentAssetPortfolioMarginEnabled) {
          return true;
        }
        return false;
      }
    }

    return false;
  };

  const onButtonClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    activeSide: string
  ) => {
    const side = activeSide === 'buy' ? ORDER_SIDE.BUY : ORDER_SIDE.SELL;
    if (whenBelowMd) {
      handleBtnClick?.(side);
    }
    handleBuySellClick(side, isTableHoverButton);

    event.stopPropagation();
  };

  return showButtons() ? (
    <div
      className={cn(styles.container, {
        [className]: className,
      })}
      onMouseOver={onMouseOver}
      onFocus={noop}
      data-palette="BuySellHoverButton">
      <button
        type="button"
        data-testid="buy-hover-button"
        className={cn(styles.buyButton, buyBtnClassName || '', {
          [styles.buyActive]: buyActive,
        })}
        onClick={e => onButtonClick(e, 'buy')}
        disabled={disableBtn}
      >
        {buyActive && !disableBtn ? <TickIcon /> : buyLabel}
      </button>
      <button
        type="button"
        data-testid="sell-hover-button"
        className={cn(styles.sellButton, sellBtnClassName || '', {
          [styles.sellActive]: sellActive,
        })}
        onClick={e => onButtonClick(e, 'sell')}
        disabled={disableBtn}
      >
        {sellActive && !disableBtn ? <TickIcon /> : sellLabel}
      </button>
    </div>
  ) : null;
};

export default BuySellHoverButton;
